/**
 * UserDashboard.js - Teacher Dashboard (Redesigned)
 * 
 * Shows the weekly schedule for teachers with a cleaner design.
 * Now includes "Today's Children" section showing students in assigned rooms.
 * Navigation is now handled in the Header tabs.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import DaySelector from '../components/DaySelector';
import dataStore from '../data/dataStore';

function UserDashboard() {
  const { user } = useAuth();
  const { theme, colors, darkMode } = useTheme();
  const isMobile = useIsMobile();
  
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [schedule, setSchedule] = useState({});
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [myPTO, setMyPTO] = useState([]);
  const [todaysChildren, setTodaysChildren] = useState([]);
  const [myTodayRooms, setMyTodayRooms] = useState([]);
  const [weekTheme, setWeekTheme] = useState('');
  const [todaysNotes, setTodaysNotes] = useState([]);
  const [selectedMobileDay, setSelectedMobileDay] = useState(null); // For mobile day selector
  const [announcements, setAnnouncements] = useState([]); // Weekly announcements
  const [weeklyRosterByDay, setWeeklyRosterByDay] = useState({}); // { 'M': { roomId: { roomName, children } }, 'T': {...}, ... }
  const [selectedRosterDay, setSelectedRosterDay] = useState('M'); // Selected day for roster card
  const [expandedRooms, setExpandedRooms] = useState({}); // For mobile collapsible rooms
  
  // Team PTO state
  const [teamPTOMonth, setTeamPTOMonth] = useState(new Date());
  const [teamPTOEntries, setTeamPTOEntries] = useState([]);
  
  // My Weekly Schedule state
  const [myWeeklySchedule, setMyWeeklySchedule] = useState([]);
  const [isSchedulePublished, setIsSchedulePublished] = useState(false);

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
  }

  // Get today's date string (for data lookups - YYYY-MM-DD)
  function getTodayStr() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get today's date for display (MM/DD/YYYY)
  function getTodayDisplay() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  useEffect(() => {
    const allRooms = dataStore.getRooms();
    setRooms(allRooms);
    setUsers(dataStore.getUsers());
    setSchedule(dataStore.getWeekSchedule(weekStart));
    
    // Check if this week's schedule is published
    const published = dataStore.isWeekPublished(weekStart);
    setIsSchedulePublished(published);
    
    if (user) {
      setMyPTO(dataStore.getPTORequestsByUser(user.id));
      
      // Only load teacher's schedule data if the week is published
      if (!published) {
        setMyTodayRooms([]);
        setTodaysChildren([]);
        setTodaysNotes([]);
        setMyWeeklySchedule([]);
      } else {
        // Get today's schedule and find which rooms I'm assigned to
        const todayStr = getTodayStr();
        const todaySchedule = dataStore.getScheduleForDate(todayStr);
        
        // Find rooms where current user is assigned today
        const myRoomsToday = [];
      for (const roomId in todaySchedule) {
        const assignments = todaySchedule[roomId] || [];
        const myAssignment = assignments.find(a => {
          const userId = typeof a === 'object' ? a.userId : a;
          return userId === user.id;
        });
        if (myAssignment) {
          const room = allRooms.find(r => r.id === roomId);
          if (room) {
            myRoomsToday.push({
              room,
              startTime: typeof myAssignment === 'object' ? myAssignment.startTime : '08:00',
              endTime: typeof myAssignment === 'object' ? myAssignment.endTime : '17:00'
            });
          }
        }
      }
      setMyTodayRooms(myRoomsToday);
      
      // Get children in my assigned rooms for today
      const childrenData = [];
      myRoomsToday.forEach(({ room, startTime, endTime }) => {
        const childrenInRoom = dataStore.getChildrenInRoom(room.id, todayStr);
        childrenInRoom.forEach(({ child, scheduleEntry }) => {
          childrenData.push({
            child,
            scheduleEntry,
            room,
            teacherStartTime: startTime,
            teacherEndTime: endTime
          });
        });
      });
      setTodaysChildren(childrenData);
      
      // Get notes for my assigned rooms today
      const notesData = [];
      myRoomsToday.forEach(({ room }) => {
        const roomNotes = dataStore.getRoomDayNotes(room.id, todayStr);
        if (roomNotes && roomNotes.length > 0) {
          notesData.push({
            room,
            notes: roomNotes
          });
        }
      });
      setTodaysNotes(notesData);
      
      // Build My Weekly Schedule
      const weekDatesArr = dataStore.getWeekDates(weekStart);
      const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      const myWeekData = [];
      
      weekDatesArr.forEach((dateStr, index) => {
        const daySchedule = dataStore.getScheduleForDate(dateStr);
        const myRoomsThisDay = [];
        
        for (const roomId in daySchedule) {
          const assignments = daySchedule[roomId] || [];
          const myAssignment = assignments.find(a => {
            const aUserId = typeof a === 'object' ? a.userId : a;
            return aUserId === user.id;
          });
          
          if (myAssignment) {
            const room = allRooms.find(r => r.id === roomId);
            if (room) {
              myRoomsThisDay.push({
                room,
                startTime: typeof myAssignment === 'object' ? myAssignment.startTime : '08:00',
                endTime: typeof myAssignment === 'object' ? myAssignment.endTime : '17:00'
              });
            }
          }
        }
        
        // Sort rooms by start time
        myRoomsThisDay.sort((a, b) => a.startTime.localeCompare(b.startTime));
        
        myWeekData.push({
          dateStr,
          dayLabel: dayLabels[index],
          rooms: myRoomsThisDay,
          isToday: dateStr === todayStr
        });
      });
      
      setMyWeeklySchedule(myWeekData);
    }
    }
    
    // Load announcements for the week
    setAnnouncements(dataStore.getAnnouncementsForWeek(weekStart));
    
    // Load week theme (stored on Monday with type 'week_theme')
    const themeNote = dataStore.getDailyNote(weekStart);
    if (themeNote && themeNote.type === 'week_theme') {
      setWeekTheme(themeNote.note);
    } else {
      setWeekTheme('');
    }
    
    // Load roster if user has canViewAllStudents permission
    if (user?.canViewAllStudents) {
      const allRoomsData = dataStore.getRooms();
      const weekDatesArr = dataStore.getWeekDates(weekStart);
      const dayNames = ['M', 'T', 'W', 'Th', 'F'];
      const rosterByDay = {};
      
      // Build roster for each day
      dayNames.forEach((dayName, dayIndex) => {
        const dateStr = weekDatesArr[dayIndex];
        const rosterByRoom = {};
        
        allRoomsData.forEach(room => {
          const childrenInRoom = dataStore.getChildrenInRoom(room.id, dateStr);
          
          const childrenArray = childrenInRoom
            .filter(({ scheduleEntry }) => !scheduleEntry?.isAbsent)
            .map(({ child }) => child)
            .sort((a, b) => a.firstName.localeCompare(b.firstName));
          
          if (childrenArray.length > 0) {
            rosterByRoom[room.id] = {
              roomName: room.name,
              children: childrenArray
            };
          }
        });
        
        rosterByDay[dayName] = rosterByRoom;
      });
      
      setWeeklyRosterByDay(rosterByDay);
      
      // Set selected day to today if in current week, otherwise Monday
      const todayStr = getTodayStr();
      const todayIndex = weekDatesArr.indexOf(todayStr);
      if (todayIndex >= 0) {
        setSelectedRosterDay(dayNames[todayIndex]);
      } else {
        setSelectedRosterDay('M');
      }
      
      // Initialize all rooms as expanded
      const initialExpanded = {};
      allRoomsData.forEach(room => {
        initialExpanded[room.id] = true;
      });
      setExpandedRooms(initialExpanded);
    }
  }, [weekStart, user]);

  // Initialize mobile day selector to today (if in current week) or Monday
  useEffect(() => {
    const weekDatesArr = dataStore.getWeekDates(weekStart);
    const todayStr = getTodayStr();
    if (weekDatesArr.includes(todayStr)) {
      setSelectedMobileDay(todayStr);
    } else {
      setSelectedMobileDay(weekDatesArr[0]);
    }
  }, [weekStart]);

  // Load Team PTO for the selected month
  useEffect(() => {
    loadTeamPTO();
  }, [teamPTOMonth]);

  const loadTeamPTO = () => {
    const allUsers = dataStore.getUsers();
    const approvedPTO = dataStore.getPTORequestsByStatus('Approved');
    
    // Get first and last day of the selected month
    const year = teamPTOMonth.getFullYear();
    const month = teamPTOMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const formatDateStr = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    
    const firstDayStr = formatDateStr(firstDay);
    const lastDayStr = formatDateStr(lastDay);
    
    // First, collect all dates by user (to combine across multiple PTO requests)
    const datesByUser = {};
    
    approvedPTO.forEach(pto => {
      const ptoUser = allUsers.find(u => u.id === pto.userId);
      if (!ptoUser) return;
      
      // Filter dates that fall within the selected month
      const datesInMonth = pto.dates.filter(d => d >= firstDayStr && d <= lastDayStr);
      
      datesInMonth.forEach(date => {
        if (!datesByUser[pto.userId]) {
          datesByUser[pto.userId] = {
            userName: ptoUser.name,
            dates: [],
            partialDays: {} // Track partial day info by date
          };
        }
        
        // Avoid duplicates
        if (!datesByUser[pto.userId].dates.includes(date)) {
          datesByUser[pto.userId].dates.push(date);
        }
        
        // Track partial day info for this specific date
        if (pto.isPartialDay && pto.dates.length === 1 && pto.dates[0] === date) {
          datesByUser[pto.userId].partialDays[date] = {
            startTime: pto.partialStartTime,
            hours: pto.partialHours
          };
        }
      });
    });
    
    // Now group consecutive dates into ranges for each user
    const entries = [];
    
    Object.entries(datesByUser).forEach(([odInner, userData]) => {
      const sortedDates = [...userData.dates].sort();
      
      if (sortedDates.length === 0) return;
      
      let rangeStart = sortedDates[0];
      let rangeEnd = sortedDates[0];
      
      const addRange = () => {
        // Check if this is a single-day partial PTO
        const isPartial = rangeStart === rangeEnd && userData.partialDays[rangeStart];
        const partialInfo = isPartial ? userData.partialDays[rangeStart] : null;
        
        entries.push({
          id: `${odInner}-${rangeStart}`,
          userName: userData.userName,
          startDate: rangeStart,
          endDate: rangeEnd,
          isPartialDay: isPartial,
          partialStartTime: partialInfo?.startTime,
          partialHours: partialInfo?.hours
        });
      };
      
      for (let i = 1; i < sortedDates.length; i++) {
        const currentDate = new Date(sortedDates[i] + 'T12:00:00');
        const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
        const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
        
        if (diffDays === 1) {
          rangeEnd = sortedDates[i];
        } else {
          addRange();
          rangeStart = sortedDates[i];
          rangeEnd = sortedDates[i];
        }
      }
      addRange();
    });
    
    // Sort by start date
    entries.sort((a, b) => a.startDate.localeCompare(b.startDate));
    setTeamPTOEntries(entries);
  };

  // Team PTO month navigation
  const prevPTOMonth = () => {
    setTeamPTOMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const nextPTOMonth = () => {
    setTeamPTOMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  const formatPTOMonthLabel = () => {
    return teamPTOMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const weekDates = dataStore.getWeekDates(weekStart);

  const prevWeek = () => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() - 7);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setWeekStart(`${year}-${month}-${day}`);
  };

  const nextWeek = () => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + 7);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setWeekStart(`${year}-${month}-${day}`);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getMonday(new Date()));
  };

  const isCurrentWeek = () => {
    return weekStart === getMonday(new Date());
  };

  // Toggle room expansion for weekly roster (mobile)
  const toggleRoomExpanded = (roomId) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  const getUserName = (userId) => {
    const u = users.find(x => x.id === userId);
    return u ? u.name : 'Unknown';
  };

  // Format time from 24h to 12h format
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Get users who have approved PTO for a specific date (with partial day info)
  const getUsersOnPTO = (dateStr) => {
    return users.filter(user => dataStore.hasApprovedPTO(user.id, dateStr)).map(user => ({
      ...user,
      ptoDetails: dataStore.getPTODetailsForDate(user.id, dateStr)
    }));
  };

  const formatWeekLabel = () => {
    const startDate = new Date(weekStart + 'T12:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 4); // Friday
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const formatDayName = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatDayDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const pendingPTOCount = myPTO.filter(p => p.status === 'Pending').length;

  // Format a date string from YYYY-MM-DD to MM/DD/YYYY
  const formatDateShort = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Format an array of dates, grouping consecutive dates into ranges
  const formatDateRanges = (dates) => {
    if (!dates || dates.length === 0) return '-';
    
    const sortedDates = [...dates].sort();
    const ranges = [];
    let rangeStart = sortedDates[0];
    let rangeEnd = sortedDates[0];
    
    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = new Date(sortedDates[i] + 'T12:00:00');
      const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
      const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (diffDays === 1) {
        rangeEnd = sortedDates[i];
      } else {
        ranges.push({ start: rangeStart, end: rangeEnd });
        rangeStart = sortedDates[i];
        rangeEnd = sortedDates[i];
      }
    }
    ranges.push({ start: rangeStart, end: rangeEnd });
    
    return ranges.map(range => {
      if (range.start === range.end) {
        return formatDateShort(range.start);
      } else {
        return `${formatDateShort(range.start)} - ${formatDateShort(range.end)}`;
      }
    }).join(', ');
  };

  // Styles
  const containerStyle = {
    padding: isMobile ? '12px' : '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const weekNavStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px 20px',
    backgroundColor: theme.cardBackground,
    borderRadius: '8px',
    border: `1px solid ${theme.border}`
  };

  const navButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#333',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'all 0.2s ease'
  };

  const weekTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.text
  };

  const gridContainerStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    marginBottom: '24px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed'
  };

  const thStyle = {
    padding: '16px 12px',
    backgroundColor: theme.backgroundSecondary,
    textAlign: 'center',
    borderBottom: `1px solid ${theme.border}`
  };

  const dayNameStyle = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: theme.text
  };

  const dayDateStyle = {
    fontSize: '0.8rem',
    color: theme.textSecondary,
    marginTop: '4px'
  };

  const roomThStyle = {
    ...thStyle,
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#333',
    width: '140px',
    verticalAlign: 'middle'
  };

  const tdStyle = {
    padding: '12px',
    border: `1px solid ${theme.border}`,
    textAlign: 'center',
    verticalAlign: 'middle',
    backgroundColor: theme.cardBackground,
    overflow: 'hidden'
  };

  const roomTdStyle = {
    ...tdStyle,
    backgroundColor: theme.backgroundSecondary,
    padding: '10px 8px'
  };

  const roomCardColors = [
    darkMode ? '#3d5a4a' : '#e8f5e9',  // Green tint
    darkMode ? '#4a5a3d' : '#f1f8e9',  // Light green
    darkMode ? '#3d4a5a' : '#e3f2fd',  // Blue tint
    darkMode ? '#5a4a3d' : '#fff8e1'   // Amber tint
  ];

  const roomNameStyle = {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: theme.text
  };

  const roomInfoStyle = {
    fontSize: '0.7rem',
    fontWeight: '400',
    color: theme.textSecondary,
    marginTop: '2px'
  };

  const teacherTagStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    padding: '8px 12px',
    margin: '3px',
    borderRadius: '6px',
    backgroundColor: colors.green,
    color: '#333',
    fontSize: '0.95rem',
    fontWeight: '600'
  };

  const ptoTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '3px',
    borderRadius: '6px',
    backgroundColor: colors.red,
    color: '#333',
    fontSize: '0.9rem',
    fontWeight: '600'
  };

  // Partial day PTO style - uses orange/amber to differentiate from full day PTO
  const partialPtoTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '3px',
    backgroundColor: '#f59e0b', // amber/orange
    borderRadius: '6px',
    color: '#333',
    fontSize: '0.85rem',
    fontWeight: '600',
    position: 'relative',
    overflow: 'hidden'
  };

  const ptoRowStyle = {
    ...tdStyle,
    backgroundColor: theme.backgroundSecondary,
    boxShadow: `inset 0 2px 0 ${colors.red}`
  };

  const ptoLabelStyle = {
    ...roomTdStyle,
    boxShadow: `inset 0 2px 0 ${colors.red}`,
    verticalAlign: 'middle',
    textAlign: 'center'
  };

  const ptoCardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '20px 24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  };

  const ptoTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  return (
    <div style={containerStyle}>
      {/* Welcome Banner + Announcements Row */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? '12px' : '20px'
      }}>
        {/* Welcome Card */}
        <div style={{
          flex: isMobile ? 'none' : '1',
          padding: isMobile ? '14px 16px' : '20px 24px',
          backgroundColor: theme.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${theme.border}`
        }}>
          <h2 style={{ 
            fontSize: isMobile ? '1.1rem' : '1.4rem', 
            fontWeight: '600', 
            color: theme.text, 
            margin: '0 0 6px 0' 
          }}>
            Welcome back, {user?.name || 'Teacher'}! 👋
          </h2>
          <p style={{ 
            fontSize: isMobile ? '0.8rem' : '0.9rem', 
            color: theme.textSecondary, 
            margin: 0 
          }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            {weekTheme && (
              <span style={{ 
                marginLeft: isMobile ? '8px' : '16px',
                padding: '4px 12px',
                backgroundColor: darkMode ? colors.purple : colors.purple,
                borderRadius: '20px',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                color: darkMode ? '#fff' : '#333',
                fontWeight: '500'
              }}>
                🎨 {weekTheme}
              </span>
            )}
          </p>
        </div>

        {/* Announcements Card - Hidden on mobile if empty */}
        {(!isMobile || announcements.length > 0) && (
          <div style={{
            flex: isMobile ? 'none' : '1',
            padding: isMobile ? '14px 16px' : '20px 24px',
            backgroundColor: theme.cardBackground,
            borderRadius: '12px',
            border: `1px solid ${theme.border}`,
            maxHeight: isMobile ? 'none' : '150px',
            overflowY: 'auto'
          }}>
            <h3 style={{ 
              fontSize: isMobile ? '1rem' : '1.1rem', 
              fontWeight: '600', 
              color: theme.text, 
              margin: '0 0 10px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📢 Announcements
              {announcements.length > 0 && (
                <span style={{
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  backgroundColor: darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)',
                  borderRadius: '10px',
                  color: colors.blue
                }}>
                  {announcements.length}
                </span>
              )}
            </h3>
            {announcements.length === 0 ? (
              <p style={{ 
                fontSize: isMobile ? '0.8rem' : '0.85rem', 
                color: theme.textSecondary, 
                margin: 0,
                fontStyle: 'italic'
              }}>
                No announcements this week
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {announcements.map(ann => (
                  <div key={ann.id} style={{
                    fontSize: isMobile ? '0.8rem' : '0.85rem',
                    color: theme.text,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <span>{ann.icon}</span>
                    <span>{ann.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Week Navigation */}
      {isMobile && (
        <div style={{ marginBottom: '16px' }}>
          {/* Week Nav Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <button 
              style={{
                padding: '10px 14px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: colors.blue,
                color: darkMode ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: '600'
              }} 
              onClick={prevWeek}
            >
              ‹
            </button>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: '600', color: theme.text }}>
                {formatWeekLabel()}
              </div>
              {!isCurrentWeek() && (
                <button 
                  style={{
                    marginTop: '4px',
                    padding: '4px 12px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                  onClick={goToCurrentWeek}
                >
                  Go to Today
                </button>
              )}
            </div>

            <button 
              style={{
                padding: '10px 14px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: colors.blue,
                color: darkMode ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: '600'
              }} 
              onClick={nextWeek}
            >
              ›
            </button>
          </div>

          {/* Day Selector */}
          <DaySelector
            weekDates={weekDates}
            selectedDate={selectedMobileDay}
            onSelectDate={setSelectedMobileDay}
          />
        </div>
      )}

      {/* Desktop Week Navigation */}
      {!isMobile && (
      <div style={weekNavStyle}>
        <button style={navButtonStyle} onClick={prevWeek}>
          ← Previous
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={weekTitleStyle}>{formatWeekLabel()}</span>
          {!isCurrentWeek() && (
            <button 
              style={{
                padding: '8px 16px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.backgroundSecondary,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}
              onClick={goToCurrentWeek}
            >
              Today
            </button>
          )}
        </div>

        <button style={navButtonStyle} onClick={nextWeek}>
          Next →
        </button>
      </div>
      )}

      {/* Mobile Schedule Cards */}
      {isMobile && selectedMobileDay && !isSchedulePublished && (
        <div style={{ 
          padding: '30px 20px', 
          textAlign: 'center', 
          color: theme.textSecondary,
          backgroundColor: darkMode ? 'rgba(255,167,38,0.3)' : 'rgba(255,193,100,0.85)',
          borderRadius: '12px',
          border: `2px dashed ${colors.orange}`,
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📋</div>
          <div style={{ fontWeight: '600', color: darkMode ? '#fff' : '#333', fontSize: '1.1rem' }}>Schedule Not Yet Published</div>
          <div style={{ fontSize: '0.85rem', marginTop: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#555' }}>
            This week's schedule is still being finalized by administration. Check back later!
          </div>
        </div>
      )}
      {isMobile && selectedMobileDay && isSchedulePublished && (
        <div style={{ marginBottom: '20px' }}>
          {rooms.map((room, roomIndex) => {
            const daySchedule = schedule[selectedMobileDay] || {};
            const assignments = daySchedule[room.id] || [];
            
            return (
              <div 
                key={room.id}
                style={{
                  backgroundColor: theme.cardBackground,
                  borderRadius: '12px',
                  marginBottom: '12px',
                  border: `1px solid ${theme.border}`,
                  overflow: 'hidden'
                }}
              >
                {/* Room Header */}
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: roomCardColors[roomIndex % roomCardColors.length],
                  borderBottom: `1px solid ${theme.border}`
                }}>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: theme.text 
                  }}>
                    🏫 {room.name}
                  </div>
                  {(room.ratio || room.ageRange) && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: theme.textSecondary,
                      marginTop: '2px'
                    }}>
                      {room.ratio ? `Ratio ${room.ratio}:1` : ''}
                      {room.ratio && room.ageRange ? ' • ' : ''}
                      {room.ageRange ? room.ageRange.displayText : ''}
                    </div>
                  )}
                </div>
                
                {/* Assignments */}
                <div style={{ padding: '12px 16px' }}>
                  {assignments.length === 0 ? (
                    <div style={{ 
                      color: theme.textSecondary, 
                      fontStyle: 'italic',
                      fontSize: '0.9rem'
                    }}>
                      No teachers assigned
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {assignments.map(assignment => {
                        const odInner = typeof assignment === 'object' ? assignment.userId : assignment;
                        const startTime = typeof assignment === 'object' ? assignment.startTime : null;
                        const endTime = typeof assignment === 'object' ? assignment.endTime : null;
                        const assignKey = typeof assignment === 'object' ? assignment.id : odInner;
                        
                        return (
                          <div 
                            key={assignKey}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 14px',
                              backgroundColor: colors.green,
                              color: '#333',
                              borderRadius: '8px'
                            }}
                          >
                            <span style={{ fontWeight: '600' }}>{getUserName(odInner)}</span>
                            {startTime && endTime && (
                              <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                                {formatTime(startTime)} - {formatTime(endTime)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* PTO for selected day (mobile) */}
          {(() => {
            const usersOnPTOToday = getUsersOnPTO(selectedMobileDay);
            if (usersOnPTOToday.length === 0) return null;
            
            // Helper to format time for PTO display
            const formatPtoTime = (timeStr) => {
              if (!timeStr) return '';
              const [h, m] = timeStr.split(':').map(Number);
              const amPm = h >= 12 ? 'PM' : 'AM';
              const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
              return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
            };
            
            return (
              <div style={{
                backgroundColor: theme.cardBackground,
                borderRadius: '12px',
                padding: '16px',
                border: `1px solid ${theme.border}`,
                borderLeft: `4px solid ${colors.red}`
              }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: '600', 
                  color: theme.text,
                  marginBottom: '10px'
                }}>
                  🏖️ On PTO Today
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {usersOnPTOToday.map(u => {
                    const isPartial = u.ptoDetails?.isPartialDay;
                    const ptoStart = u.ptoDetails?.startTime;
                    const ptoHours = u.ptoDetails?.hours;
                    
                    return (
                      <span 
                        key={u.id}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: isPartial ? '#f59e0b' : colors.red,
                          color: darkMode ? '#fff' : '#333',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}
                        title={isPartial 
                          ? `Partial PTO: ${formatPtoTime(ptoStart)} (${ptoHours}h)` 
                          : 'Full day PTO'}
                      >
                        {u.name}
                        {isPartial && (
                          <span style={{ marginLeft: '6px', fontSize: '0.75rem', opacity: 0.9 }}>
                            {formatPtoTime(ptoStart)} ({ptoHours}h)
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Desktop Schedule Table */}
      {!isMobile && !isSchedulePublished && (
        <div style={{ 
          padding: '40px 30px', 
          textAlign: 'center', 
          color: theme.textSecondary,
          backgroundColor: darkMode ? 'rgba(255,167,38,0.3)' : 'rgba(255,193,100,0.85)',
          borderRadius: '12px',
          border: `2px dashed ${colors.orange}`,
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📋</div>
          <div style={{ fontWeight: '600', color: darkMode ? '#fff' : '#333', fontSize: '1.2rem' }}>Schedule Not Yet Published</div>
          <div style={{ fontSize: '0.9rem', marginTop: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#555' }}>
            This week's schedule is still being finalized by administration. Check back later!
          </div>
        </div>
      )}
      {!isMobile && isSchedulePublished && (
      <div style={gridContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={roomThStyle}>Room</th>
              {weekDates.map((date) => (
                <th key={date} style={thStyle}>
                  <div style={dayNameStyle}>{formatDayName(date)}</div>
                  <div style={dayDateStyle}>{formatDayDate(date)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rooms.map((room, roomIndex) => (
              <tr key={room.id}>
                <td style={roomTdStyle}>
                  <div style={{
                    padding: '10px 12px',
                    backgroundColor: roomCardColors[roomIndex % roomCardColors.length],
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={roomNameStyle}>🏫 {room.shortName}</div>
                    {(room.ratio || room.ageRange) && (
                      <div style={roomInfoStyle}>
                        {room.ratio ? `${room.ratio}:1` : ''}{room.ratio && room.ageRange ? ' • ' : ''}{room.ageRange ? room.ageRange.displayText : ''}
                      </div>
                    )}
                  </div>
                </td>
                {weekDates.map(date => {
                  const daySchedule = schedule[date] || {};
                  const assignments = daySchedule[room.id] || [];
                  return (
                    <td key={date} style={tdStyle}>
                      {assignments.length === 0 ? (
                        <span style={{ color: theme.textSecondary }}>—</span>
                      ) : (
                        assignments.map(assignment => {
                          // Handle both old format (userId string) and new format (assignment object)
                          const userId = typeof assignment === 'object' ? assignment.userId : assignment;
                          const startTime = typeof assignment === 'object' ? assignment.startTime : null;
                          const endTime = typeof assignment === 'object' ? assignment.endTime : null;
                          const assignKey = typeof assignment === 'object' ? assignment.id : userId;
                          
                          return (
                            <div 
                              key={assignKey} 
                              style={teacherTagStyle}
                            >
                              <span>{getUserName(userId)}</span>
                              {startTime && endTime && (
                                <span style={{ fontSize: '0.8rem', opacity: 0.85 }}>
                                  {formatTime(startTime)}-{formatTime(endTime)}
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* On PTO Row */}
            <tr>
              <td style={ptoLabelStyle}>On PTO</td>
              {weekDates.map(date => {
                const usersOnPTO = getUsersOnPTO(date);
                
                // Helper to format time for PTO display
                const formatPtoTime = (timeStr) => {
                  if (!timeStr) return '';
                  const [h, m] = timeStr.split(':').map(Number);
                  const amPm = h >= 12 ? 'PM' : 'AM';
                  const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                  return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                };
                
                return (
                  <td key={`pto-${date}`} style={ptoRowStyle}>
                    {usersOnPTO.length === 0 ? (
                      <span style={{ color: theme.textSecondary }}>—</span>
                    ) : (
                      usersOnPTO.map(user => {
                        const isPartial = user.ptoDetails?.isPartialDay;
                        const ptoStart = user.ptoDetails?.startTime;
                        const ptoHours = user.ptoDetails?.hours;
                        
                        return (
                          <span 
                            key={user.id} 
                            style={isPartial ? partialPtoTagStyle : ptoTagStyle}
                            title={isPartial 
                              ? `Partial PTO: ${formatPtoTime(ptoStart)} (${ptoHours}h)` 
                              : 'Full day PTO'}
                          >
                            {user.name}
                            {isPartial && (
                              <span style={{ marginLeft: '6px', fontSize: '0.75rem', opacity: 0.9 }}>
                                {formatPtoTime(ptoStart)} ({ptoHours}h)
                              </span>
                            )}
                          </span>
                        );
                      })
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      )}

      {/* Today's Children + PTO Side by Side */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        
        {/* Today's Children Section */}
        <div style={{ ...ptoCardStyle, flex: '1', minWidth: '300px' }}>
          <div style={ptoTitleStyle}>
            <span>👶</span>
            Today's Students
          </div>
          
          {!isSchedulePublished ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: theme.textSecondary,
              backgroundColor: darkMode ? 'rgba(255,167,38,0.3)' : 'rgba(255,193,100,0.85)',
              borderRadius: '8px',
              border: `2px dashed ${colors.orange}`
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📋</div>
              <div style={{ fontWeight: '500', color: darkMode ? '#fff' : '#333' }}>Schedule Not Yet Published</div>
              <div style={{ fontSize: '0.8rem', marginTop: '4px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#555' }}>
                Check back later for your assignments.
              </div>
            </div>
          ) : myTodayRooms.length === 0 ? (
            <p style={{ color: theme.textSecondary, margin: 0 }}>
              You are not assigned to any rooms today ({getTodayDisplay()}).
            </p>
          ) : todaysChildren.length === 0 ? (
            <p style={{ color: theme.textSecondary, margin: 0 }}>
              No students scheduled in your rooms today.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {myTodayRooms.map(({ room, startTime, endTime }) => {
                const childrenInThisRoom = todaysChildren.filter(c => c.room.id === room.id);
                if (childrenInThisRoom.length === 0) return null;
                
                return (
                  <div key={room.id}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px',
                      paddingBottom: '8px',
                      borderBottom: `1px solid ${theme.border}`
                    }}>
                      <span style={{ fontSize: '1rem' }}>🏫</span>
                      <span style={{ fontWeight: '600', color: theme.text }}>{room.name}</span>
                      <span style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
                        ({formatTime(startTime)} - {formatTime(endTime)})
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {childrenInThisRoom.map(({ child, scheduleEntry }) => {
                        const age = dataStore.calculateChildAge(child.dateOfBirth);
                        const isVisit = scheduleEntry?.isVisit;
                        const isAbsent = scheduleEntry?.isAbsent;
                        const childStart = scheduleEntry?.startTime || child.customHours?.start || '08:00';
                        const childEnd = scheduleEntry?.endTime || child.customHours?.end || '17:00';
                        
                        return (
                          <div key={child.id} style={{
                            padding: '10px 14px',
                            backgroundColor: isAbsent
                              ? (darkMode ? 'rgba(239,83,80,0.25)' : 'rgba(239,83,80,0.15)')
                              : isVisit 
                                ? (darkMode ? 'rgba(255,167,38,0.25)' : 'rgba(255,167,38,0.15)') 
                                : theme.backgroundSecondary,
                            borderRadius: '8px',
                            borderLeft: isAbsent 
                              ? `4px solid ${colors.red}` 
                              : isVisit 
                                ? `4px solid ${colors.orange}` 
                                : `4px solid transparent`
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontWeight: '600', color: theme.text }}>
                                {child.firstName} {child.lastName}
                              </span>
                              {isAbsent && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  backgroundColor: colors.red,
                                  color: '#333',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  Absent
                                </span>
                              )}
                              {isVisit && !isAbsent && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  backgroundColor: colors.orange,
                                  color: darkMode ? '#fff' : '#333',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  ↗ Visiting
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                              {age?.displayText || 'Age unknown'} • {formatTime(childStart)} - {formatTime(childEnd)}
                            </div>
                            {child.notes && (
                              <div style={{ 
                                fontSize: '0.8rem', 
                                color: theme.text,
                                marginTop: '6px',
                                padding: '6px 8px',
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                borderRadius: '4px',
                                fontStyle: 'italic'
                              }}>
                                📝 {child.notes}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's Notes Section */}
        <div style={{ ...ptoCardStyle, flex: '1', minWidth: '300px' }}>
          <div style={ptoTitleStyle}>
            <span>📝</span>
            Today's Notes
          </div>
          
          {!isSchedulePublished ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: theme.textSecondary,
              backgroundColor: darkMode ? 'rgba(255,167,38,0.3)' : 'rgba(255,193,100,0.85)',
              borderRadius: '8px',
              border: `2px dashed ${colors.orange}`
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📋</div>
              <div style={{ fontWeight: '500', color: darkMode ? '#fff' : '#333' }}>Schedule Not Yet Published</div>
              <div style={{ fontSize: '0.8rem', marginTop: '4px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#555' }}>
                Check back later for notes.
              </div>
            </div>
          ) : myTodayRooms.length === 0 ? (
            <p style={{ color: theme.textSecondary, margin: 0 }}>
              You are not assigned to any rooms today.
            </p>
          ) : todaysNotes.length === 0 ? (
            <p style={{ color: theme.textSecondary, margin: 0 }}>
              No notes for your rooms today.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {todaysNotes.map(({ room, notes }, roomIndex) => (
                <div key={room.id}>
                  {/* Room separator line for multiple rooms */}
                  {roomIndex > 0 && (
                    <div style={{ 
                      borderTop: `2px solid ${theme.border}`, 
                      marginBottom: '12px',
                      paddingTop: '4px'
                    }} />
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px'
                  }}>
                    <span style={{ fontSize: '1rem' }}>🏫</span>
                    <span style={{ fontWeight: '600', color: theme.text }}>{room.name}</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {notes.map(note => (
                      <div key={note.id} style={{
                        padding: '10px 14px',
                        backgroundColor: theme.backgroundSecondary,
                        borderRadius: '8px',
                        borderLeft: `3px solid ${colors.blue}`
                      }}>
                        <p style={{ 
                          margin: '0 0 6px 0', 
                          color: theme.text,
                          fontSize: '0.9rem'
                        }}>
                          {note.text}
                        </p>
                        <p style={{ 
                          margin: 0, 
                          color: theme.textSecondary,
                          fontSize: '0.75rem'
                        }}>
                          — {note.createdByName} • {new Date(note.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Student Roster - Only visible if user has canViewAllStudents permission */}
        {user?.canViewAllStudents && (
          <div style={{ ...ptoCardStyle, flex: '1', minWidth: '300px', maxWidth: isMobile ? '100%' : '450px' }}>
            {/* Header with title and print button */}
            <div style={{ ...ptoTitleStyle, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📋</span>
                Weekly Student Roster
              </div>
              <button
                onClick={() => {
                  const dayNames = ['M', 'T', 'W', 'Th', 'F'];
                  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
                  
                  // Build columns for each day
                  let columnsHtml = '';
                  dayNames.forEach((day, idx) => {
                    const dayRoster = weeklyRosterByDay[day] || {};
                    const totalStudents = Object.values(dayRoster).reduce((sum, r) => sum + r.children.length, 0);
                    
                    columnsHtml += `<div class="day-column"><div class="day-header">${dayLabels[idx]}<br/><span class="count">${totalStudents}</span></div>`;
                    
                    if (Object.keys(dayRoster).length === 0) {
                      columnsHtml += `<div class="no-students">-</div>`;
                    } else {
                      Object.entries(dayRoster).forEach(([roomId, { roomName, children }]) => {
                        columnsHtml += `<div class="room"><div class="room-header">${roomName} (${children.length})</div>`;
                        children.forEach(child => {
                          columnsHtml += `<div class="student">${child.firstName} ${child.lastName}</div>`;
                        });
                        columnsHtml += `</div>`;
                      });
                    }
                    columnsHtml += `</div>`;
                  });
                  
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Weekly Student Roster</title>
                        <style>
                          @page { size: landscape; margin: 0.4in; }
                          * { box-sizing: border-box; }
                          body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }
                          h1 { text-align: center; margin: 0 0 2px 0; font-size: 14pt; }
                          .week-label { text-align: center; color: #666; margin: 0 0 10px 0; font-size: 10pt; }
                          .columns { display: flex; gap: 8px; }
                          .day-column { flex: 1; border: 1px solid #ccc; border-radius: 4px; overflow: hidden; }
                          .day-header { background: #333; color: #fff; text-align: center; padding: 6px 4px; font-weight: bold; font-size: 10pt; }
                          .day-header .count { font-size: 9pt; font-weight: normal; }
                          .room { border-bottom: 1px solid #ddd; }
                          .room:last-child { border-bottom: none; }
                          .room-header { background: #f0f0f0; padding: 4px 6px; font-weight: bold; font-size: 8pt; border-bottom: 1px solid #ddd; }
                          .student { padding: 2px 6px; font-size: 8pt; border-bottom: 1px solid #eee; }
                          .student:last-child { border-bottom: none; }
                          .no-students { padding: 10px; text-align: center; color: #999; font-size: 8pt; }
                          @media print { 
                            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                          }
                        </style>
                      </head>
                      <body>
                        <h1>Weekly Student Roster</h1>
                        <p class="week-label">${formatWeekLabel()}</p>
                        <div class="columns">${columnsHtml}</div>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }}
                style={{
                  padding: '4px 10px',
                  backgroundColor: colors.blue,
                  color: darkMode ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                🖨️ Print Week
              </button>
            </div>
            
            {/* Day Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '4px', 
              marginBottom: '15px',
              borderBottom: `1px solid ${theme.border}`,
              paddingBottom: '10px'
            }}>
              {['M', 'T', 'W', 'Th', 'F'].map(day => {
                const dayRoster = weeklyRosterByDay[day] || {};
                const totalStudents = Object.values(dayRoster).reduce((sum, r) => sum + r.children.length, 0);
                const isSelected = selectedRosterDay === day;
                
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedRosterDay(day)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      backgroundColor: isSelected ? colors.green : 'transparent',
                      color: isSelected ? (darkMode ? '#fff' : '#333') : theme.text,
                      border: isSelected ? 'none' : `1px solid ${theme.border}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: isSelected ? '600' : '400',
                      fontSize: '0.8rem'
                    }}
                  >
                    <div>{day}</div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{totalStudents}</div>
                  </button>
                );
              })}
            </div>
            
            {/* Students for selected day */}
            {(() => {
              const dayRoster = weeklyRosterByDay[selectedRosterDay] || {};
              
              if (Object.keys(dayRoster).length === 0) {
                return (
                  <p style={{ color: theme.textSecondary, margin: 0 }}>
                    No students scheduled for {selectedRosterDay === 'Th' ? 'Thursday' : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'][['M', 'T', 'W', 'Th', 'F'].indexOf(selectedRosterDay)]}.
                  </p>
                );
              }
              
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Object.entries(dayRoster).map(([roomId, { roomName, children }]) => (
                    <div key={roomId} style={{
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {/* Room Header */}
                      <div 
                        onClick={() => isMobile && toggleRoomExpanded(roomId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          cursor: isMobile ? 'pointer' : 'default'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '1rem' }}>🏫</span>
                          <span style={{ fontWeight: '600', color: theme.text }}>{roomName}</span>
                          <span style={{ 
                            fontSize: '0.75rem', 
                            color: theme.textSecondary,
                            backgroundColor: theme.backgroundSecondary,
                            padding: '2px 8px',
                            borderRadius: '10px'
                          }}>
                            {children.length}
                          </span>
                        </div>
                        {isMobile && (
                          <span style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                            {expandedRooms[roomId] ? '▼' : '▶'}
                          </span>
                        )}
                      </div>
                      
                      {/* Children List */}
                      {(!isMobile || expandedRooms[roomId]) && (
                        <div style={{ padding: '8px 12px' }}>
                          {children.map((child) => (
                            <div key={child.id} style={{
                              padding: '6px 0',
                              borderBottom: `1px solid ${theme.border}`
                            }}>
                              <span style={{ 
                                color: theme.text, 
                                fontSize: '0.85rem'
                              }}>
                                {child.firstName} {child.lastName}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div> {/* End Today's Children/Notes/Roster container */}

      {/* My Weekly Schedule + My PTO + Team PTO Row */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
        
        {/* My Weekly Schedule */}
        <div style={{ ...ptoCardStyle, flex: '1', minWidth: '280px' }}>
          <div style={ptoTitleStyle}>
            <span>📅</span>
            My Weekly Schedule
          </div>
          
          {!isSchedulePublished ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: theme.textSecondary,
              backgroundColor: darkMode ? 'rgba(255,167,38,0.3)' : 'rgba(255,193,100,0.85)',
              borderRadius: '8px',
              border: `2px dashed ${colors.orange}`
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📋</div>
              <div style={{ fontWeight: '500', color: darkMode ? '#fff' : '#333' }}>Schedule Not Yet Published</div>
              <div style={{ fontSize: '0.8rem', marginTop: '4px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#555' }}>
                This week's schedule is still being finalized. Check back later!
              </div>
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {myWeeklySchedule.map(day => (
              <div 
                key={day.dateStr}
                style={{
                  padding: '8px 10px',
                  backgroundColor: day.isToday 
                    ? (darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)')
                    : theme.backgroundSecondary,
                  borderRadius: '6px',
                  borderLeft: day.isToday 
                    ? `3px solid ${colors.blue}` 
                    : `3px solid transparent`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ 
                    fontWeight: '600', 
                    color: day.isToday ? colors.blue : theme.text,
                    minWidth: '75px',
                    fontSize: '0.85rem'
                  }}>
                    {day.dayLabel}
                  </span>
                  {day.isToday && (
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '1px 5px',
                      backgroundColor: colors.blue,
                      color: '#333',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      Today
                    </span>
                  )}
                  {day.rooms.length === 0 ? (
                    <span style={{ color: theme.textSecondary, fontSize: '0.8rem', fontStyle: 'italic' }}>
                      Not scheduled
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                      {day.rooms.map((r, i) => (
                        <span key={r.room.id}>
                          {i > 0 && ', '}
                          <span style={{ color: theme.text }}>{r.room.name}</span>
                          {' '}({formatTime(r.startTime)}-{formatTime(r.endTime)})
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* PTO Summary */}
        <div style={{ ...ptoCardStyle, flex: '1', minWidth: '280px' }}>
        <div style={ptoTitleStyle}>
          <span>📋</span>
          My PTO Requests
          {pendingPTOCount > 0 && (
            <span style={{
              backgroundColor: colors.blue,
              color: darkMode ? '#fff' : '#333',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.75rem'
            }}>
              {pendingPTOCount} pending
            </span>
          )}
        </div>
        
        {myPTO.length === 0 ? (
          <p style={{ color: theme.textSecondary, margin: 0 }}>
            No PTO requests submitted yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {myPTO.slice(0, 5).map(pto => {
              // Format partial day time
              const formatPtoTime = (timeStr) => {
                if (!timeStr) return '';
                const [h, m] = timeStr.split(':').map(Number);
                const amPm = h >= 12 ? 'PM' : 'AM';
                const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
              };
              
              return (
                <div key={pto.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  backgroundColor: theme.backgroundSecondary,
                  borderRadius: '8px',
                  borderLeft: pto.isPartialDay ? '3px solid #f59e0b' : 'none'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ color: theme.text, fontSize: '0.9rem' }}>
                      {formatDateRanges(pto.dates)}
                    </span>
                    {pto.isPartialDay && (
                      <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>
                        ⏰ {formatPtoTime(pto.partialStartTime)} ({pto.partialHours}h)
                      </span>
                    )}
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: pto.status === 'Approved' ? colors.green : 
                                     pto.status === 'Denied' ? colors.red : colors.blue,
                    color: '#333'
                  }}>
                    {pto.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Team PTO Card */}
        <div style={{ ...ptoCardStyle, flex: '1', minWidth: '280px' }}>
          <div style={ptoTitleStyle}>
            <span>👥</span>
            Team PTO
          </div>
          
          {/* Month Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <button
              type="button"
              onClick={prevPTOMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ‹
            </button>
            <span style={{ color: theme.text, fontWeight: 'bold', fontSize: '0.95rem' }}>
              {formatPTOMonthLabel()}
            </span>
            <button
              type="button"
              onClick={nextPTOMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ›
            </button>
          </div>
          
          {/* PTO Entries List */}
          {teamPTOEntries.length === 0 ? (
            <p style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>No approved PTO this month.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '350px', overflowY: 'auto' }}>
              {teamPTOEntries.map(entry => {
                // Format date for display
                const formatEntryDate = (dateStr) => {
                  const [year, month, day] = dateStr.split('-');
                  return `${month}/${day}/${year}`;
                };
                
                const dateDisplay = entry.startDate === entry.endDate
                  ? formatEntryDate(entry.startDate)
                  : `${formatEntryDate(entry.startDate)} - ${formatEntryDate(entry.endDate)}`;
                
                return (
                  <div 
                    key={entry.id}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: theme.backgroundSecondary,
                      borderRadius: '6px',
                      borderLeft: `3px solid ${colors.purple || '#9b59b6'}`,
                      fontSize: '0.85rem',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ fontWeight: '600', color: colors.purple || '#9b59b6' }}>
                      {dateDisplay}
                    </span>
                    <span>{entry.userName}</span>
                    {entry.isPartialDay && (
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: theme.textSecondary,
                        backgroundColor: theme.hover,
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {(() => {
                          const [h, m] = entry.partialStartTime.split(':').map(Number);
                          const amPm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                          return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                        })()} ({entry.partialHours}h)
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div> {/* End My PTO + Team PTO Row */}
    </div>
  );
}

export default UserDashboard;
